import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { PageHeader, Empty } from "@/components/Kit";
import { useAcademicYear, useAcademicYearMutations } from "@/lib/academicYear";
import { CalendarClock, Plus, Check, Trash2, ArrowUpRight, GraduationCap, RotateCcw } from "lucide-react";

const GRAD = "__graduate__";
const RETAIN = "__retain__";

export default function AcademicYear() {
  const { years, current, next } = useAcademicYear();
  const { add, setCurrent, remove } = useAcademicYearMutations();
  const [newYear, setNewYear] = useState("");

  return (
    <div data-testid="academic-year-page" className="space-y-5">
      <PageHeader title="Academic Year" subtitle="Set the running year and roll students up to the next class" />

      {/* Years */}
      <div className="ep-card p-5">
        <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-white">
          <CalendarClock size={16} className="text-indigo-400" /> Academic years
        </div>
        <div className="flex flex-wrap gap-2">
          {years.map((y) => (
            <div key={y} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${y === current ? "border-indigo-500 bg-indigo-500/10 text-white" : "border-slate-700 bg-slate-800/50 text-slate-300"}`} data-testid={`ay-${y}`}>
              <span className="font-semibold">{y}</span>
              {y === current ? (
                <span className="rounded bg-indigo-500 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white">Current</span>
              ) : (
                <>
                  <button className="text-emerald-400 hover:text-emerald-300" title="Make current"
                    onClick={() => setCurrent.mutate(y, { onSuccess: () => toast.success(`Current year is now ${y}`), onError: (e) => toast.error(apiError(e.response?.data?.detail)) })}
                    data-testid={`ay-setcurrent-${y}`}><Check size={14} /></button>
                  <button className="text-slate-500 hover:text-rose-400" title="Remove"
                    onClick={() => remove.mutate(y, { onError: (e) => toast.error(apiError(e.response?.data?.detail)) })}
                    data-testid={`ay-del-${y}`}><Trash2 size={13} /></button>
                </>
              )}
            </div>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <input className="ep-input w-36" placeholder="2027-28" value={newYear}
            onChange={(e) => setNewYear(e.target.value.replace(/[^\d-]/g, "").slice(0, 7))} data-testid="ay-new-input" />
          <button className="ep-btn ep-btn-ghost" disabled={add.isPending || !/^\d{4}-\d{2}$/.test(newYear)}
            onClick={() => add.mutate(newYear, { onSuccess: () => { setNewYear(""); toast.success("Year added"); }, onError: (e) => toast.error(apiError(e.response?.data?.detail)) })}
            data-testid="ay-add-btn"><Plus size={15} /> Add year</button>
          {next && !years.includes(next) && (
            <button className="text-xs text-indigo-400 hover:text-indigo-300" onClick={() => setNewYear(next)}>use {next}</button>
          )}
        </div>
      </div>

      <Promote current={current} next={next} />
    </div>
  );
}

function Promote({ current, next }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [toYear, setToYear] = useState(next);
  const [map, setMap] = useState({});
  const [held, setHeld] = useState({}); // studentId -> true

  const { data: preview } = useQuery({
    queryKey: ["promote-preview"],
    queryFn: async () => (await api.get("/school/promote/preview")).data,
    enabled: open,
  });
  const { data: students = [] } = useQuery({
    queryKey: ["students"],
    queryFn: async () => (await api.get("/school/students")).data,
    enabled: open,
  });

  // seed the mapping from the server's default once the preview loads
  useEffect(() => {
    if (preview) {
      setMap((prev) => (Object.keys(prev).length ? prev : Object.fromEntries(preview.rows.map((r) => [r.from, r.to]))));
      setToYear((y) => y || preview.suggested_year);
    }
  }, [preview]);

  const activeByClass = useMemo(() => {
    const g = {};
    students.filter((s) => s.status === "active").forEach((s) => {
      (g[s.class_name || "—"] ||= []).push(s);
    });
    return g;
  }, [students]);

  const run = useMutation({
    mutationFn: async () => (await api.post("/school/promote", {
      to_year: toYear,
      mapping: map,
      retained_ids: Object.keys(held).filter((k) => held[k]),
      set_current: true,
    })).data,
    onSuccess: (r) => {
      toast.success(`${r.promoted} promoted · ${r.graduated} graduated · ${r.retained} retained. Year is now ${toYear}.`, { duration: 7000 });
      qc.invalidateQueries();
      setOpen(false);
      setMap({});
      setHeld({});
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const classOptions = preview?.classes || [];

  return (
    <div className="ep-card p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 text-sm font-semibold text-white"><ArrowUpRight size={16} className="text-emerald-400" /> Promote students to next year</p>
          <p className="mt-1 text-xs text-slate-400">Move every active student up one class (e.g. UKG → 1st). Highest class → graduated. Hold back individual students below.</p>
        </div>
        {!open && <button className="ep-btn" onClick={() => setOpen(true)} data-testid="promote-open-btn">Start promotion</button>}
      </div>

      {open && (
        <div className="mt-5 space-y-4">
          <label className="block max-w-xs">
            <span className="mb-1.5 block text-xs font-medium text-slate-400">New academic year</span>
            <input className="ep-input" value={toYear} placeholder="2027-28"
              onChange={(e) => setToYear(e.target.value.replace(/[^\d-]/g, "").slice(0, 7))} data-testid="promote-toyear" />
          </label>

          {!preview ? <Empty>Loading classes…</Empty> : (
            <div className="overflow-x-auto rounded-lg border border-slate-800">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-800 bg-slate-800/40 text-left text-xs uppercase tracking-wider text-slate-500">
                    <th className="px-3 py-2.5">This year</th>
                    <th className="px-3 py-2.5">Students</th>
                    <th className="px-3 py-2.5">Next year → goes to</th>
                    <th className="px-3 py-2.5">Hold back</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r) => {
                    const roster = activeByClass[r.from] || [];
                    return (
                      <tr key={r.from} className="border-b border-slate-800/50 align-top">
                        <td className="px-3 py-2.5 font-medium text-white">{r.from}</td>
                        <td className="px-3 py-2.5 text-slate-400">{r.students}</td>
                        <td className="px-3 py-2.5">
                          <select className="ep-input !py-1.5 text-sm" value={map[r.from] ?? r.to}
                            onChange={(e) => setMap({ ...map, [r.from]: e.target.value })}
                            data-testid={`promote-map-${r.from}`}>
                            {classOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                            <option value={GRAD}>🎓 Graduated / passed out</option>
                            <option value={RETAIN}>↺ Stay in {r.from}</option>
                          </select>
                        </td>
                        <td className="px-3 py-2.5">
                          {roster.length === 0 ? <span className="text-xs text-slate-600">—</span> : (
                            <details>
                              <summary className="cursor-pointer text-xs text-indigo-400">
                                {roster.filter((s) => held[s.id]).length} of {roster.length}
                              </summary>
                              <div className="mt-1 max-h-40 space-y-1 overflow-y-auto pr-1">
                                {roster.map((s) => (
                                  <label key={s.id} className="flex items-center gap-2 text-xs text-slate-300">
                                    <input type="checkbox" className="h-3 w-3 accent-amber-500" checked={!!held[s.id]}
                                      onChange={(e) => setHeld({ ...held, [s.id]: e.target.checked })} />
                                    {s.name} <span className="text-slate-600">{s.admission_no}</span>
                                  </label>
                                ))}
                              </div>
                            </details>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3 rounded-lg bg-amber-500/5 border border-amber-500/30 p-3 text-xs text-amber-300">
            <RotateCcw size={14} className="shrink-0" />
            This changes every active student's class right away and sets the running year to <b className="mx-1">{toYear}</b>. Do it once, at the start of the new session.
          </div>
          <div className="flex gap-2">
            <button className="ep-btn" disabled={run.isPending || !/^\d{4}-\d{2}$/.test(toYear)} onClick={() => run.mutate()} data-testid="promote-run-btn">
              <GraduationCap size={16} /> {run.isPending ? "Promoting…" : `Promote & start ${toYear}`}
            </button>
            <button className="ep-btn ep-btn-ghost" onClick={() => { setOpen(false); setMap({}); setHeld({}); }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
